import { Button } from "@/lib/components/Button/Button";
import { Input } from "@/lib/components/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { VoiceActorConfig } from "@repo/api/voice-actor";
import { useState, useMemo, useCallback } from "react";
import toasts from "@/lib/components/Toast";
import { RadioGroup, RadioGroupItem } from "@/lib/components/RadioGroup";
import { Label } from "@/lib/components/Label";

const PROVIDER_OPTIONS = [
  {
    value: "ELEVENLABS_V4",
    label: "ElevenLabs V4",
  },
] as const satisfies ReadonlyArray<{
  value: VoiceActorConfig["provider"];
  label: string;
}>;

function CreateVoiceActor() {
  const {
    voiceId,
    provider,
    isGlobal,
    setVoiceId,
    setProvider,
    setIsGlobal,
    isValid,
    handleSubmit,
    reset,
    isLoading,
  } = useCreateVoiceActorForm();

  return (
    <form
      className="flex h-full flex-col py-6"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="flex flex-1 flex-col gap-4">
        <div className="flex flex-col">
          <div className="input-label">Voice ID</div>
          <Input
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            placeholder="Enter ElevenLabs voice ID"
          />
        </div>
        <div className="flex flex-col">
          <div className="input-label">ElevenLabs provider</div>
          <Select
            value={provider}
            onValueChange={(value) =>
              setProvider(value as VoiceActorConfig["provider"])
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a provider" />
            </SelectTrigger>
            <SelectContent>
              {PROVIDER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col">
          <div className="input-label">Scope</div>
          <RadioGroup
            value={isGlobal ? "global" : "team"}
            onValueChange={(value) => setIsGlobal(value === "global")}
            className="ml-0.25 flex flex-col gap-3"
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="team" id="scope-team" />
              <Label htmlFor="scope-team">Add to current team</Label>
            </div>
            <div className="flex items-center gap-3">
              <RadioGroupItem value="global" id="scope-global" />
              <Label htmlFor="scope-global">Add globally to all teams</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <Button
          onClick={handleSubmit}
          disabled={!isValid || isLoading}
          isLoading={isLoading}
        >
          Add voice actor
        </Button>
        <Button variant="outline" onClick={reset} disabled={isLoading}>
          Reset
        </Button>
      </div>
    </form>
  );
}

function useCreateVoiceActorForm() {
  const createVoiceActor = useEditorStore(
    (state) => state.service.createVoiceActor,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [voiceId, setVoiceId] = useState("");
  const [provider, setProvider] =
    useState<VoiceActorConfig["provider"]>("ELEVENLABS_V4");
  const [isGlobal, setIsGlobal] = useState(false);

  const isValid = useMemo(() => {
    return voiceId.length > 0 && provider.length > 0;
  }, [voiceId, provider]);

  const reset = useCallback(() => {
    setVoiceId("");
    setProvider("ELEVENLABS_V4");
    setIsGlobal(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!isValid || isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const config: VoiceActorConfig = {
        provider,
        voiceId,
      };

      await createVoiceActor(config, isGlobal);
      toasts.info("Voice actor added successfully");
      reset();
    } catch (error) {
      console.error(error);
      toasts.error("Failed to add voice actor");
    } finally {
      setIsLoading(false);
    }
  }, [
    voiceId,
    provider,
    isGlobal,
    isValid,
    isLoading,
    createVoiceActor,
    reset,
  ]);

  return {
    voiceId,
    provider,
    isGlobal,
    setVoiceId,
    setProvider,
    setIsGlobal,
    isValid,
    handleSubmit,
    reset,
    isLoading,
  };
}

export default CreateVoiceActor;
